import { type ContextData } from '@common/defaults/ContextData';

interface CacheConfig {
    headers: Record<string, string>;
    next: NonNullable<RequestInit['next']>;
}

interface CacheConfigContextData {
    cacheBuster?: ContextData['cacheBuster'];
    env?: ContextData['env'];
}

interface CacheConfigFetchOptions {
    next?: RequestInit['next'];
}

/**
 * Returns a cache buster header to prevent caching requests in Playwright tests.
 * We use a header instead of setting cache to "no-store" because Next.js
 * does not allow switching pages between cacheable and non-cacheable.
 */
export const getCacheConfig = (
    contextData: CacheConfigContextData,
    isClientSide: boolean,
    fetchOptions: CacheConfigFetchOptions | undefined,
): CacheConfig => {
    const appEnv = contextData.env?.APP_ENV || process.env.APP_ENV || process.env.NODE_ENV;
    const isProd = appEnv === 'production';

    const cacheConfig: CacheConfig = {
        next: {
            ...fetchOptions?.next,
            tags: isProd ? undefined : ['next-web', ...(fetchOptions?.next?.tags || [])],
        },
        headers: {},
    };

    // TODO: remove this default revalidation time on all fetches
    if (!isClientSide) {
        cacheConfig.next.revalidate = 1;
    }

    if (contextData.cacheBuster) {
        // Set cache buster header to make sure this request is unqiue
        cacheConfig.headers['x-pxr-cache-buster'] = contextData.cacheBuster;

        if (!isClientSide) {
            // Reduce revalidation time if the request is usually being cached
            if (typeof cacheConfig.next.revalidate === 'number' && cacheConfig.next.revalidate > 1) {
                cacheConfig.next.revalidate = 1;
            }
        }
    }

    return cacheConfig;
};
